import React, { useState } from 'react'
import Grid from '@mui/material/Grid'
import FormControl from '@mui/material/FormControl'
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import { grey } from '@mui/material/colors'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'

import { useAppDispatch } from 'redux/Hook'
import { AdminUITheme } from 'types/AdminUiThemeMui'
import { toggleModal as showModal } from 'redux/slices/SignUpQuestions'
import { SIGN_UP_DATE_QUESTION } from 'lib/participants/Constants'
import {
  zIndexLoweredFilterCSS,
  zIndexRaisedFilterCSS,
} from 'lib/GlobalConstants'
import CustomChip from 'components/Shared/Chip'

interface SignupFormQuestionOptions {
  [key: number | string]: any
}

interface FilterPillProps {
  question: string
  value: SignupFormQuestionOptions[]
  index: number
  type: string
  selected: string
}

const renderTags = (tagValue: any[], getTagProps: any) => {
  return tagValue.map((option, index) => {
    return (
      <CustomChip
        key={index}
        filterValues={[option.name]}
        size="small"
        extraProps={{
          ...getTagProps({ index }),
          color: 'primary',
        }}
      />
    )
  })
}

const FilterPills = (props: FilterPillProps) => {
  const { index, value, question, type, selected } = props
  const theme: AdminUITheme = useTheme()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [cssUpdates, setCssUpdates] = useState<any>(zIndexLoweredFilterCSS)
  const handleClick = () => dispatch(showModal())

  const editIcon = (
    <ModeEditOutlinedIcon
      sx={{
        cursor: 'pointer',
        color: grey[600],
        fontSize: '1.25rem',
      }}
      onClick={handleClick}
    />
  )

  const getFilterLabel = ({
    question,
    type,
    selected,
  }: {
    question: string
    type: string
    selected: string
  }) => {
    if (type === SIGN_UP_DATE_QUESTION) {
      if (selected === 'before') {
        return `${t('participant.signUpQuestions.dateBefore')}: ${question}`
      }

      return `${t('participant.signUpQuestions.dateAfter')}: ${question}`
    }

    return question
  }

  /**
   if the value is undefined do not render the filter pill this will happen when group
   created with signup form question is removed in the future.
  **/
  if (!value) return <></>

  return (
    <>
      <Grid item key={index}>
        <FormControl data-testid="sign-up-questions-filter-pill">
          <Autocomplete
            sx={{
              ...{
                '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                  pr: 3,
                },
              },
              ...theme.GenericTheme.dynamicFilterWrapperWidth,
            }}
            multiple
            options={value || []}
            value={value}
            getOptionLabel={(option) => option.name}
            filterSelectedOptions
            limitTags={1}
            renderTags={renderTags}
            popupIcon={editIcon}
            openText={t('filters.ariaTexts.open')}
            closeText={t('filters.ariaTexts.close')}
            clearText={t('filters.ariaTexts.clear')}
            readOnly
            size="small"
            onClick={handleClick}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  ...theme.GenericTheme.textFieldCSSToRaiseZindex,
                  ...cssUpdates,
                  ...theme.GenericTheme.dynamicFilterWrapperWidth,
                }}
                label={getFilterLabel({ question, type, selected })}
              />
            )}
            onFocus={() => {
              setCssUpdates(zIndexRaisedFilterCSS)
            }}
            onBlur={() => {
              setCssUpdates(zIndexLoweredFilterCSS)
            }}
          />
        </FormControl>
      </Grid>
    </>
  )
}

export default FilterPills
